/**
 * The merchant-side kiosk payment pairing: wire shapes of `payment/providers/filter` and
 * `payment/provider/:pid/configuration` → what the setup form offers.
 *
 * Ported from rista-merchant-dashboard's `helpers/kiosk-helpers.ts`, where this screen used to
 * live. Pure — no store, no platform APIs.
 */

import type { KioskDevice } from "./deviceTypes";

/** A `kioskdevice/*` body that is a real device record, not an error envelope or `null`. */
export function isKioskDevice(payload: any): payload is KioskDevice {
  return !!payload && typeof payload === "object" && !payload.code && Array.isArray(payload.payment);
}

/**
 * `kioskdevice/fetch` → the setup page's list. That call answers THIS panel's one record (found
 * by the `deviceIdentifier` header) or `null` when nobody has paired it; an array is read as-is
 * too, so the page needs no change if the list ever comes from `kioskdevice/filter`.
 */
export const toKioskDeviceList = (response: any): KioskDevice[] => {
  const rows = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [response];
  return rows.filter(isKioskDevice);
};

/** Provider types the EDC section may offer. An allowlist: anything else is not rendered. */
export const EDC_PROVIDER_TYPES = ["pinelabs", "razorpayedc", "paytmedc", "phonepeedc", "bonushubedc"];

/**
 * EDC provider types paired with no card machine and no mode: their configuration is empty, and
 * the POS's own flow has no terminal step for them either (`@rista/payments/providers/phonepeedc`).
 */
export const EDC_PROVIDER_TYPES_WITHOUT_SETUP = ["phonepeedc"];

/** Provider types the UPI section may offer. */
export const UPI_PROVIDER_TYPES = ["phonepeupi"];

export interface PairingProvider {
  pid: string;
  /** The provider KEY (`pinelabs`, `phonepeupi`, …) — what the scope allowlists match on. */
  providerType: string;
  name: string;
  /**
   * The logo as the row carries it (`meta.img`) — a path on the payment-icon host or an absolute
   * URL, `""` when there is none. Resolved by the VIEW with `getPaymentIconsUrl`: that helper
   * reads the store-backed `catalogueHelpers`, which this pure module (imported by the kiosk
   * saga) must not pull in.
   */
  img: string;
}

export interface PairingTerminal {
  deviceId: string;
  label: string;
}

export interface PairingMode {
  /** `String(value)` — a stable key for the radio list. */
  id: string;
  name: string;
  /** Sent back untouched; numeric on every provider seen so far. */
  value: string | number;
}

export interface PairingConfiguration {
  terminals: PairingTerminal[];
  modes: PairingMode[];
}

export const EMPTY_PAIRING_CONFIGURATION: PairingConfiguration = { terminals: [], modes: [] };

/** The rows of a list response, whichever envelope it arrived in. */
const responseRows = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  const data = response?.data ?? response?.rows ?? response?.content;
  return Array.isArray(data) ? data : [];
};

/**
 * `payment/providers/filter` → providers. The live list nests its ids under `meta` and names the
 * type `mode`; the documented flat shape (`pid`, `mid`) is read as a fallback.
 */
export const toPairingProviders = (response: any): PairingProvider[] =>
  responseRows(response)
    // A provider switched off in admin is not offered.
    .filter((row) => row?.enable !== false)
    .map((row) => {
      const pid = String(row?.meta?.pid ?? row?.pid ?? "");
      const providerType = String(row?.mode ?? row?.mid ?? "");
      return {
        pid,
        providerType,
        name: row?.label || row?.name || providerType || pid,
        img: String(row?.meta?.img ?? row?.img ?? ""),
      };
    })
    // A row with no pid cannot be paired against.
    .filter((provider) => Boolean(provider.pid));

const isOfType = (provider: PairingProvider, types: string[]) =>
  types.includes(provider.providerType.toLowerCase());

export const isEdcProvider = (provider: PairingProvider) => isOfType(provider, EDC_PROVIDER_TYPES);
export const isUpiProvider = (provider: PairingProvider) => isOfType(provider, UPI_PROVIDER_TYPES);

/** Whether an EDC provider is saved only once a card machine (and a mode, if it sends any) is picked. */
export const edcNeedsMachineSetup = (providerType: string | undefined): boolean =>
  !EDC_PROVIDER_TYPES_WITHOUT_SETUP.includes((providerType ?? "").toLowerCase());

/** `payment/provider/:pid/configuration` → the provider's terminals and modes. */
export const toPairingConfiguration = (response: any): PairingConfiguration => {
  const data = response?.data ?? response ?? {};
  const devices: any[] = Array.isArray(data?.devices) ? data.devices : [];
  const modes: any[] = Array.isArray(data?.modes) ? data.modes : [];

  return {
    terminals: devices
      .filter((terminal) => Boolean(terminal?.deviceId))
      .map((terminal) => ({ deviceId: String(terminal.deviceId), label: terminal.label || String(terminal.deviceId) })),
    modes: modes
      // 0 is a legitimate mode code, so only null/undefined counts as "no mode".
      .filter((mode) => mode?.value !== undefined && mode?.value !== null)
      .map((mode) => ({ id: String(mode.value), name: mode.name || String(mode.value), value: mode.value })),
  };
};

/** Documented `kioskdevice/save` error codes → what the merchant is told. */
export const KIOSK_DEVICE_ERROR_MESSAGES: Record<string, string> = {
  REST_ERR_INVALID_INPUT: "Invalid input. Please check the values entered.",
  REST_INVALID_DEVICE: "This device no longer exists or has been deleted.",
  ACCESS_DENIED: "Your login is not allowed to change kiosk payment setup.",
};

/** The message for a failed save — a documented code's own, else the fallback. */
export const getPairingErrorMessage = (error: any, fallback: string): string => {
  const code = error?.code ?? error?.response?.data?.code;
  return (code && KIOSK_DEVICE_ERROR_MESSAGES[code]) || fallback;
};
